import * as Tone from 'tone';
import { Midi } from '@tonejs/midi';
import { createNESNoisePool } from './nes-noise';

const DEFAULT_BPM = 90; // <--- Set your desired BPM here
const SQ1_VOLUME = -32;
const SQ2_VOLUME = -32;
const TRIANGLE_VOLUME = -13;
const NOISE_VOLUME = -20;

class BackgroundMusic {
    constructor() {
        // Square Wave 1
        this.square1Pool = Array(8).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'square', width: 0.5 },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.3, release: 0.1 }
        }).toDestination());
        this.square1Pool.forEach(synth => synth.volume.value = SQ1_VOLUME);
        this.currentSquare1Index = 0;

        // Square Wave 2
        this.square2Pool = Array(8).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'square', width: 0.5 },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.3, release: 0.1 }
        }).toDestination());
        this.square2Pool.forEach(synth => synth.volume.value = SQ2_VOLUME);
        this.currentSquare2Index = 0;

        // Triangle Wave
        this.trianglePool = Array(8).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'triangle' },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.5, release: 0.1 }
        }).toDestination());
        this.trianglePool.forEach(synth => synth.volume.value = TRIANGLE_VOLUME);
        this.currentTriangleIndex = 0;

        // Noise
        this.noisePool = createNESNoisePool(8);
        this.noisePool.forEach(noise => noise.volume.value = NOISE_VOLUME);
        this.currentNoiseIndex = 0;

        // Transport
        this.transport = Tone.getTransport();
        this.transport.bpm.value = DEFAULT_BPM;  // Use our BPM constant
        this.parts = new Map();
        this.initialized = false;

        // This will hold our computed total loop length in ticks
        this.loopLengthInTicks = 0;
    }

    getNextSynth(pool, currentIndexProp) {
        const synth = pool[this[currentIndexProp]];
        this[currentIndexProp] = (this[currentIndexProp] + 1) % pool.length;
        return synth;
    }

    async loadTonalTrack(midiData, trackName, synthPool, indexProp) {
        try {
            const midi = new Midi(midiData);
            const midiTrack = midi.tracks[0];

            if (!midiTrack) {
                console.warn(`No track found in MIDI file for ${trackName}`);
                return null;
            }

            // Use ticks-based scheduling
            const notes = midiTrack.notes.map(note => ({
                time: `${note.ticks}i`,              // e.g. "1024i"
                note: note.name,
                duration: `${note.durationTicks}i`,  // e.g. "480i"
                velocity: note.velocity
            }));

            const part = new Tone.Part((time, note) => {
                const synth = this.getNextSynth(synthPool, indexProp);
                try {
                    synth.triggerAttackRelease(note.note, note.duration, time, note.velocity);
                } catch (e) { /* skip note on timing conflict at loop boundary */ }
            }, notes);

            // Enable looping in ticks
            part.loop = true;
            part.loopEnd = `${this.loopLengthInTicks}i`; // e.g. "1920i" for 2 bars @ 960 PPQ
            part.start(0);

            this.parts.set(trackName, part);
            return part;
        } catch (error) {
            console.error(`Error loading MIDI file for ${trackName}:`, error);
            return null;
        }
    }

    async loadNoiseTrack(midiData) {
        try {
            const midi = new Midi(midiData);
            const midiTrack = midi.tracks[0];

            if (!midiTrack) {
                console.warn('No track found in noise MIDI file');
                return null;
            }

            const notes = midiTrack.notes.map(note => ({
                time: `${note.ticks}i`,
                duration: `${note.durationTicks}i`,
                velocity: note.velocity
            }));

            const part = new Tone.Part((time, note) => {
                const noiseSynth = this.getNextSynth(this.noisePool, 'currentNoiseIndex');
                try {
                    noiseSynth.triggerAttackRelease(note.duration, time, note.velocity);
                } catch (e) { /* skip note on timing conflict at loop boundary */ }
            }, notes);

            part.loop = true;
            part.loopEnd = `${this.loopLengthInTicks}i`;
            part.start(0);

            this.parts.set('noise', part);
            return part;
        } catch (error) {
            console.error('Error loading noise MIDI file:', error);
            return null;
        }
    }

    async init(midiFiles) {
        if (this.initialized) return;

        try {
            await Tone.start();
            this.transport.stop();
            this.transport.position = 0;

            // Determine the maximum ticks among all files
            let maxTicks = 0;
            let masterPPQ = 0;

            const allFiles = [midiFiles.square1, midiFiles.square2, midiFiles.triangle, midiFiles.noise];
            for (const file of allFiles) {
                if (file) {
                    const midi = new Midi(file);
                    // Use the first file’s PPQ as the “master” (assuming all share the same PPQ)
                    if (!masterPPQ) {
                        masterPPQ = midi.header.ppq;
                        // Set Tone.Transport.PPQ so time: "xxxi" lines up correctly
                        Tone.Transport.PPQ = masterPPQ;
                    }
                    maxTicks = Math.max(maxTicks, midi.durationTicks);
                }
            }

            // Round up to the next bar
            // 4 beats per bar => 4 * PPQ ticks in one bar
            const ticksPerBar = 4 * masterPPQ;
            const neededBars = Math.ceil(maxTicks / ticksPerBar);
            this.loopLengthInTicks = neededBars * ticksPerBar;

            // Load each track with ticks-based scheduling
            const tracks = [
                { data: midiFiles.square1, name: 'square1', pool: this.square1Pool, indexProp: 'currentSquare1Index' },
                { data: midiFiles.square2, name: 'square2', pool: this.square2Pool, indexProp: 'currentSquare2Index' },
                { data: midiFiles.triangle, name: 'triangle', pool: this.trianglePool, indexProp: 'currentTriangleIndex' }
            ];

            for (const track of tracks) {
                if (track.data) {
                    await this.loadTonalTrack(track.data, track.name, track.pool, track.indexProp);
                }
            }

            if (midiFiles.noise) {
                await this.loadNoiseTrack(midiFiles.noise);
            }

            this.initialized = true;
        } catch (error) {
            console.error("Error initializing background music:", error);
        }
    }

    async play() {
        if (!this.initialized) {
            await this.init();
        }
        this.transport.stop();
        this.transport.position = 0;
        this.transport.start("+0.1");
    }

    pause() {
        this.transport.pause();
    }

    // Stop individual parts without touching the transport (for death music handoff)
    stopParts() {
        this.parts.forEach(part => part.stop());
    }

    resumeParts() {
        this.parts.forEach(part => part.start(0));
    }

    stop() {
        this.transport.stop();
        this.transport.position = 0;
    }

    setVolume(track, value) {
        const poolMap = {
            'square1': this.square1Pool,
            'square2': this.square2Pool,
            'triangle': this.trianglePool,
            'noise': this.noisePool,
            'master': 'all'
        };

        if (track === 'master') {
            Object.values(poolMap).forEach(pool => {
                if (pool !== 'all') {
                    pool.forEach(synth => synth.volume.value = value);
                }
            });
        } else {
            const pool = poolMap[track];
            if (pool) {
                pool.forEach(synth => synth.volume.value = value);
            }
        }
    }

    setTempo(bpm) {
        const wasPlaying = this.transport.state === 'started';
        const currentPosition = this.transport.position;

        // Stop, update, resume if playing
        this.transport.stop();
        this.transport.bpm.value = bpm;
        this.transport.position = currentPosition;

        if (wasPlaying) {
            this.transport.start("+0.1");
        }

        console.log(`Tempo set to ${bpm} BPM`);
    }

    dispose() {
        this.parts.forEach(part => part.dispose());
        this.square1Pool.forEach(synth => synth.dispose());
        this.square2Pool.forEach(synth => synth.dispose());
        this.trianglePool.forEach(synth => synth.dispose());
        this.noisePool.forEach(synth => synth.dispose());
        this.initialized = false;
    }
}

export default BackgroundMusic;
